/**
 * Reminder operations — CRUD for task reminders.
 */

import { ReminderKind, SourceType, TriggerType } from "./db-types.js"
import type { DbReminder } from "./db-types.js"
import { getDb, getNow, emitEvent } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { parseDbDate, toMsTimestamp } from "./utils.js"

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export async function addReminder(
  taskId: string,
  fireAt: string,
  kind: ReminderKind = ReminderKind.Reminder,
  fields?: {
    title?: string
    body?: string
    link?: string
    parentPath?: string
    /** Use this id instead of a random one (see AddTaskOpts.id). */
    id?: string
  },
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    // Stored as stringified UTC ms like every date (ISO 8601 and ms numbers are accepted): the app
    // reads fireAt with Number(), so a raw ISO string would never fire.
    const fireAtMs = toMsTimestamp(fireAt)
    if (fireAtMs === undefined) return fail(`Invalid reminder time: ${String(fireAt)}`)

    const task = await db.totodo.get(taskId)
    if (!task || task.isDeleted === 1) return fail(`Task not found: ${taskId}`)

    const id = fields?.id ?? crypto.randomUUID()
    const timestamp = getNow()

    const parentPath = fields?.parentPath || task.parentPath || ""

    const reminder: DbReminder = {
      id,
      taskId,
      parentPath,
      owner: "",
      title: fields?.title,
      body: fields?.body,
      link: fields?.link,
      sourceType: SourceType.Internal,
      triggerType: TriggerType.Time,
      fireAt: fireAtMs,
      kind,
      createdAt: timestamp,
      createdBy: "",
      isDeleted: 0,
    }

    await db.reminder.add(reminder)
    emitEvent("reminder_created", { id, taskId, kind })
    return ok({ id })
  } catch (e) {
    return fail(
      `addReminder failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getTaskReminder(
  taskId: string,
): Promise<DbReminder | undefined> {
  const db = getDb()
  return db.reminder.where("[taskId+isDeleted]").equals([taskId, 0]).first()
}

/** Every live reminder of a task, earliest first (a task can hold several: multi-offset recurring ones). */
export async function getTaskReminders(taskId: string): Promise<DbReminder[]> {
  const db = getDb()
  const rows = await db.reminder.where("[taskId+isDeleted]").equals([taskId, 0]).toArray()
  return rows.sort((a, b) => (parseDbDate(a.fireAt) ?? 0) - (parseDbDate(b.fireAt) ?? 0) || a.id.localeCompare(b.id))
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export async function updateReminder(
  reminderId: string,
  updates: Partial<
    Pick<DbReminder, "fireAt" | "kind" | "title" | "body" | "link" | "parentPath">
  >,
): Promise<OpResult> {
  const db = getDb()
  try {
    const existing = await db.reminder.get(reminderId)
    if (!existing || existing.isDeleted === 1) return fail(`Reminder not found: ${reminderId}`)

    const changes = { ...updates }
    if (updates.fireAt !== undefined) {
      const fireAtMs = toMsTimestamp(updates.fireAt)
      if (fireAtMs === undefined) return fail(`Invalid reminder time: ${String(updates.fireAt)}`)
      changes.fireAt = fireAtMs
    }
    await db.reminder.update(reminderId, {
      ...changes,
      updatedAt: getNow(),
    })
    return ok()
  } catch (e) {
    return fail(
      `updateReminder failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export async function deleteReminder(reminderId: string): Promise<OpResult> {
  const db = getDb()
  try {
    const existing = await db.reminder.get(reminderId)
    if (!existing) return fail(`Reminder not found: ${reminderId}`)
    if (existing.isDeleted === 1) return ok() // already gone: deleting twice is a no-op
    const timestamp = getNow()
    await db.reminder.update(reminderId, {
      isDeleted: 1,
      deletedAt: timestamp,
      updatedAt: timestamp,
    })
    return ok()
  } catch (e) {
    return fail(
      `deleteReminder failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function deleteTaskReminders(taskId: string): Promise<OpResult> {
  const db = getDb()
  try {
    const timestamp = getNow()
    const reminders = await db.reminder
      .where("[taskId+isDeleted]")
      .equals([taskId, 0])
      .toArray()

    if (reminders.length === 0) return ok()

    await db.reminder.bulkUpdate(
      reminders.map((r) => ({
        key: r.id,
        changes: {
          isDeleted: 1 as const,
          deletedAt: timestamp,
          updatedAt: timestamp,
        },
      })),
    )
    return ok()
  } catch (e) {
    return fail(
      `deleteTaskReminders failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Dedup — collapse duplicate reminders per task (single source of truth)
// ---------------------------------------------------------------------------

/** Minimal shape needed to deduplicate reminders by task. */
export interface DedupableReminder {
  id?: string
  taskId?: string
  fireAt?: string | number | null
}

/**
 * Partition reminders into the set that should actually fire (`kept`) and the
 * stray duplicates that should be ignored / cleaned up (`dropped`).
 *
 * Background: a legacy bug (pre-deterministic-ID era) could leave two reminder
 * rows for the same recurring instance — a deterministic `rem_<taskId>` plus a
 * leftover random-UUID row. They have different ids, so the native scheduler
 * hashes them to different notification identifiers and fires BOTH (see
 * migration 004 and the iOS NativeReminderSync `instanceKey` derivation).
 *
 * Rule per taskId (matches migration 004 + the historical client reconcile):
 *  - 0 or 1 reminder → keep as-is.
 *  - Has deterministic `rem_<taskId>*` reminders → keep ALL of them (legitimate
 *    multi-offset alarms: `rem_<id>`, `rem_<id>_1`, …) and drop every other row.
 *  - Only non-deterministic rows → keep the one with the latest `fireAt`, drop
 *    the rest (the data model is one reminder per one-off task; see
 *    `getTaskReminder`, which assumes a single row).
 *
 * This is the single source of truth: the client (`useReminderSync`) and the
 * server (`/v1/reminders/pending`) must dedupe identically so foreground and
 * native background scheduling never disagree.
 */
export function partitionDuplicateReminders<T extends DedupableReminder>(
  reminders: T[],
): { kept: T[]; dropped: T[] } {
  const byTask = new Map<string, T[]>()
  const kept: T[] = []
  const dropped: T[] = []

  for (const r of reminders) {
    // No task association → can't be a per-task duplicate; always keep.
    if (!r.taskId) {
      kept.push(r)
      continue
    }
    const group = byTask.get(r.taskId)
    if (group) group.push(r)
    else byTask.set(r.taskId, [r])
  }

  for (const [taskId, group] of byTask) {
    if (group.length <= 1) {
      kept.push(...group)
      continue
    }
    const deterministicPrefix = `rem_${taskId}`
    const deterministic = group.filter((r) => r.id?.startsWith(deterministicPrefix))
    const random = group.filter((r) => !r.id?.startsWith(deterministicPrefix))

    if (deterministic.length > 0) {
      // Keep all deterministic (multi-offset) reminders; drop random duplicates.
      kept.push(...deterministic)
      dropped.push(...random)
    } else {
      // No deterministic row → keep only the latest fireAt, drop the rest.
      const sorted = [...random].sort(
        (a, b) => (parseDbDate(b.fireAt) ?? 0) - (parseDbDate(a.fireAt) ?? 0),
      )
      kept.push(sorted[0]!)
      dropped.push(...sorted.slice(1))
    }
  }

  return { kept, dropped }
}
